/**
 * The chapter editor (decisions 2, 26, 29; table E in the part 09 log): CodeMirror 6 over one chapter's source, with
 * the tree from part 02's parser, table D's live decorations, the OS spellchecker, autosave and the writer's input
 * (part 10: shortcuts, escaping as the writer types, paste, undo), and images put in at marks (part 16b). The app
 * shell that opens and closes chapters is part 11's.
 */
import { normalizeSource } from '@cockatrice/core';
import type { ParsedChapter, TextChange } from '@cockatrice/core';
import { isolateHistory } from '@codemirror/commands';
import { Compartment, EditorSelection, EditorState, Transaction } from '@codemirror/state';
import type { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import type { ViewUpdate } from '@codemirror/view';
import { Autosave } from './autosave.ts';
import type { AutosaveResult, AutosaveStatus, Schedule } from './autosave.ts';
import { chapterField, textChanges } from './chapter-state.ts';
import { currentMatch, findMarks, setCurrentMatch, showMatches, shownMatches } from './find.ts';
import type { TextRange } from './find.ts';
import { forgetMark, holdImageMark, imageInput, imageMarks, imagePoint, placeMark } from './images.ts';
import { noteMarkField, showNoteMarks } from './note-marks.ts';
import type { NoteMark } from './note-marks.ts';
import type { ImageFilesHandler } from './images.ts';
import { autoConvert, liveInput, settledText, writerInput } from './input.ts';
import { BLOCK_LOOK, lookView, sameLook } from './look.ts';
import type { ParagraphLook } from './look.ts';
import { liveTheme } from './theme.ts';
import { liveView } from './view.ts';

/**
 * What the live preview follows (part 15): the edit, as changes in the text before it (sorted, not overlapping, as
 * part 02's `reparseChapter` takes them; none when only the selection moved), and where the cursor is after it.
 */
export interface EditorUpdate {
  readonly changes: readonly TextChange[];
  readonly cursor: number;
}

/** What a chapter editor needs; everything with an effect is injected. */
export interface ChapterEditorOptions {
  /** Where the editor goes in the page. */
  readonly parent: Element;
  /** The chapter's stored text. */
  readonly text: string;
  /** The book's language (decision 33), for the spellchecker. */
  readonly language: string;
  /** Stores the chapter's text: part 06's `EditSession.save`, given the chapter's record. */
  readonly save: (text: string) => Promise<AutosaveResult>;
  readonly schedule: Schedule;
  readonly now: () => number;
  readonly onStatus?: (status: AutosaveStatus) => void;
  /** E10: called after each change of the text or the selection, for the preview (part 15). */
  readonly onUpdate?: (update: EditorUpdate) => void;
  /** Whether typed shortcuts convert (decision 66; table S, S7); on unless set. */
  readonly autoConversion?: boolean;
  /** The text box's accessible name (E8), in the interface's language; part 11 gives it. */
  readonly label?: string;
  /** Part 10a: the paragraph look; the block look unless set. */
  readonly paragraphLook?: ParagraphLook;
  /** Part 16b, I2, I3: a paste or drop holding image files hands them here, with where they go; none takes no files. */
  readonly onImages?: ImageFilesHandler;
}

/** E10: a view update as the preview takes it: its changes in the old text's positions, and the cursor (the main selection's head). */
export function editorUpdate(update: ViewUpdate): EditorUpdate {
  return { changes: textChanges(update.changes), cursor: update.state.selection.main.head };
}

/** E3: the OS spellchecker, autocorrect and sentence capitals on, in the book's language. */
export function spellcheck(language: string): Extension {
  return EditorView.contentAttributes.of({ spellcheck: 'true', autocorrect: 'on', autocapitalize: 'sentences', lang: language });
}

/** Table E: one chapter open for editing. */
export class ChapterEditor {
  readonly view: EditorView;
  private readonly autosave: Autosave;
  private readonly editable = new Compartment();
  private readonly conversion = new Compartment();
  private readonly look = new Compartment();
  /** The look shown (part 10a). */
  private shown: ParagraphLook;
  /** Whether the app stopped input ({@link pause}), which a failed close keeps. */
  private paused = false;
  /** Whether the view is gone ({@link close}). */
  private closed = false;
  /** The last mark's ID (part 16b). */
  private marks = 0;

  constructor(options: ChapterEditorOptions) {
    // E1: CodeMirror keeps only `\n` line ends, so the text it holds is the normalised one; when that differs from
    // what was stored, the difference is saved like any edit.
    const text = normalizeSource(options.text);
    this.shown = options.paragraphLook ?? BLOCK_LOOK;
    this.autosave = new Autosave({
      // Principle 1 (part 10): a composition in progress is stored as it will settle, canonical.
      read: () => settledText(this.view.state),
      save: options.save,
      schedule: options.schedule,
      now: options.now,
      onStatus: options.onStatus,
    });
    this.view = new EditorView({
      parent: options.parent,
      state: EditorState.create({
        doc: text,
        extensions: [
          liveView(),
          liveInput(),
          this.conversion.of(autoConvert.of(options.autoConversion ?? true)),
          this.look.of(lookView(this.shown)),
          liveTheme,
          EditorView.lineWrapping,
          spellcheck(options.language),
          // E8: assistive technology names the text box; CodeMirror gives it the textbox role and no name.
          options.label === undefined ? [] : EditorView.contentAttributes.of({ 'aria-label': options.label }),
          this.editable.of([]),
          imageMarks,
          findMarks,
          noteMarkField,
          options.onImages === undefined ? [] : imageInput(options.onImages),
          // E2: every change of the text reaches the autosave; a selection alone doesn't (E6).
          EditorView.updateListener.of((update) => {
            if (update.docChanged) this.autosave.changed();
            if (options.onUpdate !== undefined && (update.docChanged || update.selectionSet)) options.onUpdate(editorUpdate(update));
          }),
        ],
      }),
    });
    if (text !== options.text) this.autosave.changed();
  }

  /** The text and its tree, always a full parse of the text (E2). */
  get parsed(): ParsedChapter {
    return this.view.state.field(chapterField);
  }

  /** Where the writer's text stands. */
  get status(): AutosaveStatus {
    return this.autosave.status;
  }

  /** S7: switches typed shortcuts' conversion on or off. */
  setAutoConversion(on: boolean): void {
    this.view.dispatch({ effects: this.conversion.reconfigure(autoConvert.of(on)) });
  }

  /** Part 10a, S3: shows the text in `look` from now on; the same look again changes nothing. */
  setParagraphLook(look: ParagraphLook): void {
    if (sameLook(look, this.shown)) return;
    this.shown = look;
    this.view.dispatch({ effects: this.look.reconfigure(lookView(look)) });
  }

  /**
   * E9: makes the text due a save again though it didn't change: for text that was kept somewhere else (part 11: a
   * conflict copy) and must still reach the chapter.
   */
  resave(): void {
    this.autosave.changed();
  }

  /**
   * Part 16b, I1–I3, I9: keeps the place `at` (the cursor's when not given) as a mark that follows the writer's edits,
   * where an image will go once it has imported; returns the mark's ID.
   */
  markImage(at: number = this.view.state.selection.main.head): number {
    this.marks += 1;
    this.view.dispatch({ effects: placeMark(this.marks, Math.max(0, Math.min(at, this.view.state.doc.length))) });
    return this.marks;
  }

  /**
   * Part 16b, I6, I8, I10, I13: puts `markup` (an image's, `imageMarkup`) in at mark `id`, as one change the writer can
   * undo on its own; the mark then stands after it, so the next image of the batch follows. A cursor standing at the
   * mark moves after the image; any other selection stays. False, writing nothing, when the editor is closed or paused
   * or the mark is gone.
   */
  insertImage(id: number, markup: string): boolean {
    const placed = this.closed || this.paused ? undefined : this.view.state.field(imageMarks).get(id);
    if (placed === undefined) return false;
    // Deep-fix r2, r3, tables K and B: never inside another construct or a block's markup.
    const mark = { ...placed, pos: imagePoint(this.parsed, placed.pos) };
    const { main } = this.view.state.selection;
    const follow = main.empty && main.head === placed.pos;
    // The mark moves past what goes in at it, so it ends after the text the filter writes for the image.
    this.view.dispatch({ effects: placeMark(id, mark.pos, 1) });
    this.view.dispatch({
      changes: { from: mark.pos, insert: markup },
      ...(follow ? { selection: EditorSelection.cursor(mark.pos + markup.length) } : {}),
      annotations: [writerInput.of('source'), Transaction.userEvent.of('input.paste')],
      scrollIntoView: follow,
    });
    // I9: text put in at it stays after the next image again; I13: typing straight after never joins the image's change
    // in the history.
    this.view.dispatch({ effects: holdImageMark(id), annotations: isolateHistory.of('after') });
    return true;
  }

  /** Part 16b: forgets mark `id` (its batch is done). */
  releaseImage(id: number): void {
    if (!this.closed) this.view.dispatch({ effects: forgetMark(id) });
  }

  /** Part 19a, F2, F9: shows `ranges` (a flat list `[from₀, to₀, …]` of the text's positions) as matches, none current. */
  showMatches(ranges: readonly number[]): void {
    if (!this.closed) this.view.dispatch({ effects: showMatches.of(ranges) });
  }

  /** Part 19a, F9: the matches shown, mapped through every edit since they were shown, as a flat list. */
  get matches(): number[] {
    return shownMatches(this.view.state);
  }

  /** Part 19a, F5: the current match, mapped through every edit since it was selected, or null. */
  get currentMatch(): TextRange | null {
    return currentMatch(this.view.state);
  }

  /** Part 19e, MK1–MK4: shows `marks` (positions in the text now) as the notes' marks, replacing those shown. */
  showNotes(marks: readonly NoteMark[]): void {
    if (!this.closed) this.view.dispatch({ effects: showNoteMarks.of(marks) });
  }

  /**
   * Part 19e, D′11: selects `range` (clipped to the text), scrolled to the middle of the view at once, and gives the text
   * the keyboard focus, so the writer is at the note's passage. Nothing once closed.
   */
  goTo(range: TextRange): void {
    if (this.closed) return;
    const length = this.view.state.doc.length;
    const from = Math.min(Math.max(0, range.from), length);
    const to = Math.min(Math.max(from, range.to), length);
    this.view.dispatch({ selection: EditorSelection.single(from, to), effects: EditorView.scrollIntoView(from, { y: 'center' }) });
    this.view.focus();
  }

  /** Part 19a, F3: the main selection. */
  get selection(): TextRange {
    const { from, to } = this.view.state.selection.main;
    return { from, to };
  }

  /**
   * Part 19a, F3, F4: makes `range` the current match and selects it, scrolled to the middle of the view at once (no
   * animation); the keyboard focus stays where it is (the find panel). Nothing once closed.
   */
  selectMatch(range: TextRange): void {
    if (this.closed) return;
    this.view.dispatch({ selection: EditorSelection.single(range.from, range.to), effects: [setCurrentMatch.of(range), EditorView.scrollIntoView(range.from, { y: 'center' })] });
  }

  /**
   * Part 19a, F5 (principle 6): makes the text `text` (canonical, a replace's) as one change the writer can undo on its
   * own, when it holds `expected` now; the cursor goes after the change. Returns where the text changed, in the new
   * text; null, changing nothing, when the editor is closed or paused, holds another text, or a composition is under
   * way.
   */
  replaceText(expected: string, text: string): TextRange | null {
    const { state } = this.view;
    if (this.closed || this.paused || state.doc.toString() !== expected || settledText(state) !== expected) return null;
    let start = 0;
    while (start < expected.length && start < text.length && expected.charCodeAt(start) === text.charCodeAt(start)) start += 1;
    let end = 0;
    while (end < expected.length - start && end < text.length - start && expected.charCodeAt(expected.length - 1 - end) === text.charCodeAt(text.length - 1 - end)) end += 1;
    const insert = text.slice(start, text.length - end);
    this.view.dispatch({
      changes: { from: start, to: expected.length - end, insert },
      selection: EditorSelection.cursor(start + insert.length),
      annotations: [writerInput.of('rewritten'), Transaction.userEvent.of('input.replace'), isolateHistory.of('full')],
      scrollIntoView: true,
    });
    return { from: start, to: start + insert.length };
  }

  /** Saves whatever is unsaved now (table A, A9–A12): before a split, a trash or a snapshot restore (part 08). */
  flush(): Promise<AutosaveResult> {
    return this.autosave.flush();
  }

  /**
   * E7: stops (true) or restarts (false) the writer's input at once, keeping the text, its autosave and the view: for
   * a session handing the lock to another tab or reconnecting (part 06, part 11).
   */
  pause(paused: boolean): void {
    this.paused = paused;
    this.setEditable(!paused);
  }

  /**
   * E5: stops typing, saves, and closes the editor. When the save fails the editor stays open, editable unless
   * paused (E7), and the failure is returned, so the writer's text is never thrown away with the view.
   */
  async close(): Promise<AutosaveResult> {
    this.setEditable(false);
    const result = await this.autosave.flush();
    if (result.kind === 'failed') {
      this.setEditable(!this.paused);
      return result;
    }
    await this.autosave.close();
    this.closed = true;
    this.view.destroy();
    return result;
  }

  private setEditable(editable: boolean): void {
    this.view.dispatch({
      effects: this.editable.reconfigure(editable ? [] : [EditorState.readOnly.of(true), EditorView.editable.of(false)]),
    });
  }
}
